import { useMemo } from "react";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  PestCategoryKey,
  PestTypeKey,
} from "@/services/backend/qpm/types/enums";
import { PestCategoryUtil } from "@/services/backend/qpm/lib/pest-category-util";
import { PestTypeUtil } from "@/services/backend/qpm/lib/pest-type-util";
import { TargetOrganismSelection } from "@/services/backend/qpm/offers/types/offer";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";

export type TargetOrganismSelectionValue = Pick<
  TargetOrganismSelection,
  "categoryKeys" | "typeKeys"
>;

export type TargetOrganismSelectionOption = {
  key: string;
  label: string;
};

const pestCategoryKeys = new Set(Object.values(PestCategoryKey));
const pestTypeKeys = new Set(Object.values(PestTypeKey));

const PEST_CATEGORY_OPTIONS: TargetOrganismSelectionOption[] = Object.values(
  PestCategoryKey,
).map((category) => ({
  key: category,
  label: PestCategoryUtil.toLongString(category),
}));

const PEST_TYPE_OPTIONS: TargetOrganismSelectionOption[] = Object.values(
  PestTypeKey,
).map((pestType) => ({
  key: pestType,
  label: PestTypeUtil.toLongString(pestType),
}));

const PEST_SELECTION_LABEL_BY_KEY = new Map<string, string>(
  [...PEST_CATEGORY_OPTIONS, ...PEST_TYPE_OPTIONS].map((option) => [
    option.key,
    option.label,
  ]),
);

function compactSelectionLabel(
  options: TargetOrganismSelectionOption[],
  selectedKeys: string[],
): string {
  if (selectedKeys.length === 0) {
    return t("Keine Auswahl");
  }
  const labelByKey = new Map(
    options.map((option) => [option.key.toLowerCase(), option.label]),
  );
  const labels = selectedKeys
    .map((key) => labelByKey.get(key.toLowerCase()) ?? key)
    .filter((label) => label.trim().length > 0);
  if (labels.length === 0) {
    return t("Keine Auswahl");
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.length} ${t("ausgewählt")}`;
}

function normalizeSelectionKeys(keys: string[]): string[] {
  const normalized = keys
    .map((key) => key.trim().toLowerCase())
    .filter((key) => key !== "");
  return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));
}

export function normalizeTargetOrganismSelectionValue(
  value: TargetOrganismSelectionValue,
): TargetOrganismSelectionValue {
  return {
    categoryKeys: normalizeSelectionKeys(value.categoryKeys),
    typeKeys: normalizeSelectionKeys(value.typeKeys),
  };
}

export function isSameTargetOrganismSelection(
  left: TargetOrganismSelectionValue,
  right: TargetOrganismSelectionValue,
): boolean {
  return (
    JSON.stringify(left.categoryKeys) === JSON.stringify(right.categoryKeys) &&
    JSON.stringify(left.typeKeys) === JSON.stringify(right.typeKeys)
  );
}

export function deriveContractAgainstTextFromTargetOrganismSelection(
  selection: TargetOrganismSelectionValue | undefined,
  customText?: string,
): string {
  const normalizedCustomText = customText?.trim() ?? "";
  if (normalizedCustomText !== "") {
    return normalizedCustomText;
  }

  if (!selection) {
    return "";
  }

  const labels: string[] = [];
  const seen = new Set<string>();
  const appendLabel = (key: string) => {
    const normalizedKey = key.trim().toLowerCase();
    if (normalizedKey === "") {
      return;
    }
    const resolved =
      PEST_SELECTION_LABEL_BY_KEY.get(normalizedKey) ?? key.trim();
    const normalizedLabel = resolved.trim().toLowerCase();
    if (normalizedLabel === "" || seen.has(normalizedLabel)) {
      return;
    }
    seen.add(normalizedLabel);
    labels.push(resolved.trim());
  };

  selection.categoryKeys.forEach(appendLabel);
  selection.typeKeys.forEach(appendLabel);

  if (labels.length === 0) {
    return "";
  }

  labels.sort((a, b) => a.localeCompare(b, "de"));
  return labels.join(", ");
}

export function deriveTargetOrganismOptionsFromMonitorSnaps(
  monitorSnaps: Record<string, MonitorSnapDTO> | undefined,
  includeMonitor?: (monitor: MonitorSnapDTO) => boolean,
): {
  categoryOptions: TargetOrganismSelectionOption[];
  typeOptions: TargetOrganismSelectionOption[];
} {
  const categoryLabels = new Map<string, string>();
  const typeLabels = new Map<string, string>();

  Object.values(monitorSnaps ?? {}).forEach((monitor) => {
    if (includeMonitor && !includeMonitor(monitor)) {
      return;
    }

    monitor.pests.forEach((pest) => {
      const key = pest.key.trim().toLowerCase();
      if (!key) {
        return;
      }

      if (pestCategoryKeys.has(key as PestCategoryKey)) {
        categoryLabels.set(
          key,
          PestCategoryUtil.toLongString(key as PestCategoryKey),
        );
      }

      if (pestTypeKeys.has(key as PestTypeKey)) {
        typeLabels.set(key, PestTypeUtil.toLongString(key as PestTypeKey));
      }
    });
  });

  const sortByLabel = (left: string, right: string) =>
    left.localeCompare(right, "de");

  return {
    categoryOptions: Array.from(categoryLabels.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((left, right) => sortByLabel(left.label, right.label)),
    typeOptions: Array.from(typeLabels.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((left, right) => sortByLabel(left.label, right.label)),
  };
}

function TargetOrganismMultiSelect({
  title,
  options,
  selectedKeys,
  disabled,
  onChange,
}: {
  title: string;
  options: TargetOrganismSelectionOption[];
  selectedKeys: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const buttonLabel = compactSelectionLabel(options, selectedKeys);

  return (
    <div className="grid content-start gap-1.5">
      <Label className="min-h-5 text-sm">{title}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <div className="flex items-center justify-end border-b p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || selectedKeys.length === 0}
              onClick={() => onChange([])}
            >
              {t("Alle abwählen")}
            </Button>
          </div>
          <Command>
            <CommandInput placeholder={t("Suchen")} />
            <CommandList>
              <CommandEmpty>{t("Keine Treffer")}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={`${option.key} ${option.label}`}
                    onSelect={() => {
                      const next = selectedSet.has(option.key)
                        ? selectedKeys.filter((key) => key !== option.key)
                        : [...selectedKeys, option.key];
                      onChange(next);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedSet.has(option.key)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TargetOrganismSelectionEditor({
  selection,
  onChange,
  disabled,
  className,
  categoryLabel,
  typeLabel,
  categoryOptions,
  typeOptions,
}: {
  selection: TargetOrganismSelectionValue;
  onChange: (next: TargetOrganismSelectionValue) => void;
  disabled?: boolean;
  className?: string;
  categoryLabel?: string;
  typeLabel?: string;
  categoryOptions?: TargetOrganismSelectionOption[];
  typeOptions?: TargetOrganismSelectionOption[];
}) {
  const resolvedCategoryOptions = categoryOptions ?? PEST_CATEGORY_OPTIONS;
  const resolvedTypeOptions = typeOptions ?? PEST_TYPE_OPTIONS;

  return (
    <div className={cn("grid gap-3 md:grid-cols-2", className)}>
      <TargetOrganismMultiSelect
        title={categoryLabel ?? t("Schädlingskategorien")}
        options={resolvedCategoryOptions}
        selectedKeys={selection.categoryKeys}
        disabled={Boolean(disabled)}
        onChange={(nextCategoryKeys) =>
          onChange({
            ...selection,
            categoryKeys: nextCategoryKeys,
          })
        }
      />
      <TargetOrganismMultiSelect
        title={typeLabel ?? t("Schädlingsarten")}
        options={resolvedTypeOptions}
        selectedKeys={selection.typeKeys}
        disabled={Boolean(disabled)}
        onChange={(nextTypeKeys) =>
          onChange({
            ...selection,
            typeKeys: nextTypeKeys,
          })
        }
      />
    </div>
  );
}
